type LogLevel = "debug" | "info" | "warn" | "error";

type LogFields = Record<string, unknown> | undefined;

interface Logger {
  debug: (message: string, fields?: LogFields) => void;
  error: (message: string, fields?: LogFields) => void;
  info: (message: string, fields?: LogFields) => void;
  warn: (message: string, fields?: LogFields) => void;
}

interface ResolvedMode {
  isProd: boolean;
  isTest: boolean;
}

function resolveMode(): ResolvedMode {
  const env =
    (import.meta as { env?: Record<string, string | undefined> }).env ?? {};

  return {
    isProd: env.MODE === "production" || env.PROD === "true",
    isTest: env.MODE === "test" || env.VITEST === "true",
  };
}

const mode = resolveMode();

function serializeError(value: unknown): unknown {
  if (value instanceof Error) {
    return { message: value.message, name: value.name, stack: value.stack };
  }
  return value;
}

function normalizeFields(
  fields: LogFields,
): Record<string, unknown> | undefined {
  if (!fields) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    out[key] = serializeError(value);
  }
  return out;
}

function emit(level: LogLevel, message: string, fields: LogFields): void {
  const { isProd, isTest } = mode;

  if (isTest && level !== "error") {
    return;
  }

  const normalized = normalizeFields(fields);

  if (isProd) {
    const payload = JSON.stringify({
      fields: normalized,
      level,
      message,
      timestamp: new Date().toISOString(),
    });

    if (level === "error") {
      console.error(payload);
    } else if (level === "warn") {
      console.warn(payload);
    } else {
      console.log(payload);
    }
    return;
  }

  const prefix = `[${level.toUpperCase()}]`;

  if (level === "error") {
    console.error(prefix, message, normalized ?? "");
  } else if (level === "warn") {
    console.warn(prefix, message, normalized ?? "");
  } else if (level === "debug") {
    console.debug(prefix, message, normalized ?? "");
  } else {
    console.info(prefix, message, normalized ?? "");
  }
}

export const logger: Logger = {
  debug: (message, fields) => {
    emit("debug", message, fields);
  },
  error: (message, fields) => {
    emit("error", message, fields);
  },
  info: (message, fields) => {
    emit("info", message, fields);
  },
  warn: (message, fields) => {
    emit("warn", message, fields);
  },
};
